import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiHelpCircle,
  FiInfo,
  FiLoader,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import SystemAlert from "./systemAlert";
import "./system-alert.css";

const iconMap = {
  success: FiCheckCircle,
  error: FiXCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
  question: FiHelpCircle,
};

const sanitizeHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof DOMParser === "undefined") return html.replace(/<[^>]*>/g, " ");

  const documentFragment = new DOMParser().parseFromString(html, "text/html");
  documentFragment.querySelectorAll("script, style, iframe, object, embed").forEach((node) => node.remove());
  documentFragment.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      const hasUnsafeProtocol = value.startsWith(["java", "script:"].join(""));
      if (name.startsWith("on") || name === "style" || ((name === "href" || name === "src") && hasUnsafeProtocol)) {
        node.removeAttribute(attribute.name);
      }
    });
  });
  return documentFragment.body.innerHTML;
};

const SystemAlertHost = () => {
  const [request, setRequest] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const requestIdRef = useRef(null);
  requestIdRef.current = request?.id;

  useEffect(() => SystemAlert._subscribe((event) => {
    if (event.type === "open") {
      previousFocusRef.current = document.activeElement;
      setRequest(event.request);
      setInputValue(event.request.options.inputValue ?? "");
      setValidationMessage("");
      setLoading(false);
      return;
    }

    if (event.type === "close") {
      setRequest((current) => (current?.id === event.id ? null : current));
      return;
    }

    if (event.type === "loading" && event.id === requestIdRef.current) setLoading(true);
    if (event.type === "validation" && event.id === requestIdRef.current) {
      setValidationMessage(String(event.message || ""));
      setLoading(false);
    }
  }), []);

  const options = request?.options || {};
  const safeHtml = useMemo(() => sanitizeHtml(options.html), [options.html]);
  const Icon = iconMap[options.icon] || FiInfo;
  const isToast = Boolean(options.toast);
  const hasInput = Boolean(options.input);
  const showConfirmButton = options.showConfirmButton !== false;

  useEffect(() => {
    if (!request) return undefined;

    const openCallback = options.didOpen || options.onBeforeOpen;
    const animationFrame = window.requestAnimationFrame(() => {
      if (hasInput) inputRef.current?.focus();
      else dialogRef.current?.querySelector("button")?.focus();
      if (typeof openCallback === "function") openCallback(dialogRef.current);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [request?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!request || !options.timer) return undefined;
    const timeout = window.setTimeout(() => SystemAlert._dismiss(request.id, "timer"), Number(options.timer));
    return () => window.clearTimeout(timeout);
  }, [request, options.timer]);

  useEffect(() => {
    if (!request || isToast) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [request, isToast]);

  useEffect(() => {
    if (request) return undefined;
    const previousFocus = previousFocusRef.current;
    if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    previousFocusRef.current = null;
    return undefined;
  }, [request]);

  if (!request) return null;

  const dismiss = (reason) => {
    if (loading) return;
    if (typeof options.willClose === "function") options.willClose();
    SystemAlert._dismiss(request.id, reason);
  };

  const confirm = async () => {
    setValidationMessage("");
    const rawValue = hasInput ? inputValue : true;

    if (typeof options.inputValidator === "function") {
      const message = await options.inputValidator(rawValue);
      if (message) {
        setValidationMessage(String(message));
        return;
      }
    }

    let value = rawValue;
    if (typeof options.preConfirm === "function") {
      setLoading(true);
      try {
        const preparedValue = await options.preConfirm(rawValue);
        if (preparedValue === false) {
          setLoading(false);
          return;
        }
        if (preparedValue !== undefined) value = preparedValue;
      } catch (error) {
        setValidationMessage(error?.message || "No se pudo completar la acción.");
        setLoading(false);
        return;
      }
    }

    SystemAlert._confirm(request.id, value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && options.allowEscapeKey !== false) {
      event.preventDefault();
      dismiss("esc");
      return;
    }

    if (event.key !== "Tab") return;
    const focusable = dialogRef.current?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const inputProps = {
    ...(options.inputAttributes || {}),
    ref: inputRef,
    value: inputValue,
    placeholder: options.inputPlaceholder || "",
    onChange: (event) => setInputValue(event.target.value),
    "aria-describedby": validationMessage ? "system-alert-validation" : undefined,
  };

  const content = (
    <section
      className={`system-alert system-alert--${options.icon || "info"}${isToast ? " system-alert--toast" : ""}`}
      ref={dialogRef}
      role={isToast ? (options.icon === "error" ? "alert" : "status") : "alertdialog"}
      aria-modal={isToast ? undefined : "true"}
      aria-labelledby="system-alert-title"
      aria-label={!options.title ? "Alerta del sistema" : undefined}
      aria-describedby={!options.html && options.text ? "system-alert-description" : undefined}
      onKeyDown={handleKeyDown}
    >
      {options.showCloseButton && (
        <button className="system-alert__close" type="button" onClick={() => dismiss("close")} aria-label="Cerrar alerta">
          <FiX aria-hidden="true" />
        </button>
      )}

      <div className="system-alert__icon" aria-hidden="true">
        {loading ? <FiLoader className="system-alert__spinner" /> : <Icon />}
      </div>

      <div className="system-alert__content">
        {options.title && <h2 id="system-alert-title" className="system-alert__title">{options.title}</h2>}
        {options.text && <p id="system-alert-description" className="system-alert__text">{options.text}</p>}
        {safeHtml && <div className="system-alert__html" dangerouslySetInnerHTML={{ __html: safeHtml }} />}

        {hasInput && (
          <label className="system-alert__field">
            {options.inputLabel && <span>{options.inputLabel}</span>}
            {options.input === "textarea"
              ? <textarea {...inputProps} rows={4} />
              : <input {...inputProps} type={options.input === "password" ? "password" : "text"} />}
          </label>
        )}

        {validationMessage && (
          <p className="system-alert__validation" id="system-alert-validation" role="alert">
            <FiAlertTriangle aria-hidden="true" />
            <span>{validationMessage}</span>
          </p>
        )}

        {loading && !hasInput && <p className="system-alert__loading">Procesando…</p>}
      </div>

      {!isToast && (showConfirmButton || options.showCancelButton) && (
        <div className={`system-alert__actions${options.reverseButtons ? " system-alert__actions--reverse" : ""}`}>
          {options.showCancelButton && (
            <button className="system-alert__button system-alert__button--secondary" type="button" onClick={() => dismiss("cancel")} disabled={loading}>
              {options.cancelButtonText || "Cancelar"}
            </button>
          )}
          {showConfirmButton && (
            <button className="system-alert__button system-alert__button--primary" type="button" onClick={confirm} disabled={loading}>
              {loading && <FiLoader className="system-alert__button-spinner" aria-hidden="true" />}
              {options.confirmButtonText || "Aceptar"}
            </button>
          )}
        </div>
      )}
    </section>
  );

  if (isToast) return <div className="system-alert-toast-region" aria-live="polite">{content}</div>;

  return (
    <div
      className="system-alert-backdrop"
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        const canDismiss = typeof options.allowOutsideClick === "function"
          ? options.allowOutsideClick()
          : options.allowOutsideClick !== false;
        if (canDismiss) dismiss("backdrop");
      }}
    >
      {content}
    </div>
  );
};

export default SystemAlertHost;
